<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { PencilSquareIcon } from '@heroicons/vue/24/outline';
import type { HitRecord, HitRecordKind } from './caseDetail';
import { RECORD_STATUSES, recordStatusAction, type RecordStatus } from './recordStatus';
import CopyButton from './CopyButton.vue';

const props = withDefaults(
  defineProps<{
    record: HitRecord;
    note?: string;
    status?: RecordStatus;
    focused?: boolean;
    kbEnabled?: boolean;
  }>(),
  { kbEnabled: true, status: 'unreviewed' }
);

const emit = defineEmits<{
  'cycle-status': [recordId: string];
  'open-reader': [recordId: string];
  'update-note': [recordId: string, value: string];
}>();

// A record with a note always shows it, editable in place. One without stays
// a single line until the reviewer asks for it — an empty box on every row
// would make the list much harder to scan.
const composing = ref(false);
const noteRef = ref<HTMLTextAreaElement>();
const noteOpen = computed(() => Boolean(props.note) || composing.value);
const noteFieldId = computed(() => `list-note-${props.record.id}`);

function openNote() {
  composing.value = true;
  nextTick(() => noteRef.value?.focus());
}

function onNoteInput(event: Event) {
  emit('update-note', props.record.id, (event.target as HTMLTextAreaElement).value);
}

function onNoteBlur() {
  if (!props.note) composing.value = false;
}

const kindLabels: Record<HitRecordKind, string> = {
  PEP_POSITION: 'PEP POSITION',
  SANCTION: 'SANCTION',
  ARTICLE: 'ARTICLE',
  IDENTIFIER: 'IDENTIFIER',
  SOURCE: 'SOURCE',
  PROFILE: 'PROFILE',
};
const kindStyles: Record<HitRecordKind, string> = {
  PEP_POSITION: 'bg-indigo-50 text-violet-700',
  SANCTION: 'bg-red-50 text-red-700',
  ARTICLE: 'bg-amber-50 text-amber-700',
  IDENTIFIER: 'bg-indigo-50 text-sky-700',
  SOURCE: 'bg-indigo-50 text-sky-700',
  PROFILE: 'bg-gray-100 text-gray-500',
};

function hostOf(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}
</script>

<template>
  <div
    class="border-b border-l-[3px] border-gray-100 p-4 last:border-b-0"
    :class="[RECORD_STATUSES[status].row, focused ? 'border-l-indigo-600' : 'border-l-transparent']"
    data-test="hit-record-card"
    :data-record-id="record.id"
  >
    <div class="flex items-start justify-between gap-2">
      <div class="flex flex-wrap items-center gap-1.5">
        <span
          class="inline-flex w-fit flex-none items-center rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wide"
          :class="kindStyles[record.kind]"
        >
          {{ kindLabels[record.kind] }}
        </span>
        <span
          v-if="status !== 'unreviewed'"
          class="inline-flex w-fit flex-none items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase"
          :class="RECORD_STATUSES[status].pill"
          data-test="record-status-pill"
        >
          {{ RECORD_STATUSES[status].label }}
        </span>
      </div>
      <button
        v-if="!noteOpen"
        type="button"
        aria-label="Add my note"
        title="Add my note"
        class="-mt-0.5 -mr-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
        data-test="record-note-add"
        @click="openNote"
      >
        <PencilSquareIcon class="h-4 w-4" />
      </button>
    </div>
    <div class="mt-1.5 text-[13.5px] font-semibold break-words text-gray-900">
      {{ record.title }}
    </div>
    <div class="mt-0.5 text-xs text-gray-500">{{ record.meta }}</div>

    <div
      v-if="record.url"
      class="mt-2.5 flex items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5"
    >
      <a
        :href="record.url"
        target="_blank"
        rel="noopener noreferrer"
        class="min-w-0 flex-1 truncate font-mono text-[11px] text-gray-500 hover:text-gray-700"
        >{{ hostOf(record.url) }}</a
      >
      <CopyButton :value="record.url ?? ''" label="source URL" />
    </div>

    <div v-if="noteOpen" class="mt-2.5" data-test="record-note-readback">
      <label
        :for="noteFieldId"
        class="text-[10px] font-semibold tracking-wide text-gray-400 uppercase"
      >
        My note
      </label>
      <textarea
        :id="noteFieldId"
        ref="noteRef"
        rows="1"
        :value="note ?? ''"
        placeholder="Only you can see this"
        class="mt-0.5 [field-sizing:content] w-full resize-none rounded-md border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-700 outline-none placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white"
        data-test="record-note-list-field"
        @input="onNoteInput"
        @blur="onNoteBlur"
      ></textarea>
    </div>

    <div class="mt-2.5 flex flex-wrap gap-2">
      <button
        type="button"
        class="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-[12.5px] font-semibold text-white hover:bg-indigo-700"
        @click="emit('open-reader', record.id)"
      >
        Review
        <span v-if="kbEnabled" class="hidden rounded bg-white/20 px-1 text-[10px] lg:inline"
          >⏎</span
        >
      </button>
      <a
        v-if="record.url"
        :href="record.url"
        target="_blank"
        rel="noopener noreferrer"
        class="rounded-lg border border-gray-300 px-3 py-1.5 text-[12.5px] font-semibold text-gray-700 hover:bg-gray-50"
      >
        Open source ↗
      </a>
      <button
        type="button"
        class="rounded-lg border px-3 py-1.5 text-[12.5px] font-semibold"
        :class="RECORD_STATUSES[status].button"
        :aria-label="recordStatusAction(status)"
        :title="recordStatusAction(status)"
        data-test="record-status-button"
        @click="emit('cycle-status', record.id)"
      >
        {{ RECORD_STATUSES[status].label }}
      </button>
    </div>
  </div>
</template>
